import { Box, Typography } from "@mui/material"
import { useAxesTooltip, type LineSeries } from "@mui/x-charts-pro"
import type { DateRange } from "@mui/x-date-pickers-pro"
import {
  useListNsspNsspListGet,
  type Nssp,
} from "@phc/common/gen/threatAnalyticsGen"
import type { Dayjs } from "dayjs"
import { useMemo, useState } from "react"
import { dayjsUTC } from "../../../../utils/dates"
import { extraColors } from "../../../../utils/theme"
import { ChartWithFilters } from "../../../Charts/ChartWithFilters"
import {
  InfoRow,
  ItemContainer,
  ItemHeader,
  TooltipContainer,
  TooltipContentBox,
} from "../../../Charts/LegendStyles"
import {
  CLINICAL_SHAPES,
  type BaseLegendItemData,
} from "../../../Charts/legendUtils"
import { TimeLegend } from "../../../Charts/TimeLegend"
import { TimeSeriesChart } from "../../../Charts/TimeSeriesChart"
import { GenericTag } from "../../../Shared/GenericTag"
import {
  DEFAULT_SERIES_COLORS,
  type DatasetEntry,
} from "../../timeSeriesConstants"
import { fillWeeklyGaps } from "../../timeSeriesUtils"
import { DataSourceCard } from "../DataSourceCard"

interface NsspClinicalLegendItem extends BaseLegendItemData {
  shape: (typeof CLINICAL_SHAPES)[number]
}

function isNsspKey(key: string): key is keyof Nssp {
  return (
    key === "percent_visits_covid" ||
    key === "percent_visits_influenza" ||
    key === "percent_visits_rsv"
  )
}

const NsspClinicalTooltipContent = ({
  rawData,
  hiddenIds,
}: {
  rawData: Nssp[] | undefined
  hiddenIds: Set<string>
}) => {
  const tooltipData = useAxesTooltip()
  const selectedDate = tooltipData?.[0]?.axisValue

  if (!tooltipData || tooltipData.length === 0 || !selectedDate) return null

  const tooltipContent = rawData?.filter(analysis => {
    const analysisDate = dayjsUTC(analysis.week_ended_at).toDate()
    return (
      selectedDate instanceof Date &&
      analysisDate.getTime() === selectedDate.getTime()
    )
  })

  if (!tooltipContent || tooltipContent.length === 0) return null

  const analysis = tooltipContent[0]

  if (!analysis) return null

  const items = [
    {
      id: "percent_visits_covid",
      label: "COVID-19",
      value: analysis.percent_visits_covid,
    },
    {
      id: "percent_visits_influenza",
      label: "Influenza",
      value: analysis.percent_visits_influenza,
    },
    {
      id: "percent_visits_rsv",
      label: "RSV",
      value: analysis.percent_visits_rsv,
    },
  ].filter(item => !hiddenIds.has(item.id))

  if (items.length === 0) return null

  return (
    <TooltipContainer>
      <TooltipContentBox>
        <Typography align="center" variant="body2Bold">
          {`Week Ended ${dayjsUTC(selectedDate).format("M/DD/YY")}`}
        </Typography>
        {items.map(item => (
          <Box
            key={item.id}
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 0.75,
            }}
          >
            <ItemContainer>
              <ItemHeader>
                <Typography variant="small1">{item.label}</Typography>
              </ItemHeader>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <InfoRow>
                  <Typography variant="tiny" color={extraColors.medium}>
                    % Visits
                  </Typography>
                  <Typography variant="tiny">
                    {item.value != null ? `${item.value.toFixed(2)}%` : "N/A"}
                  </Typography>
                </InfoRow>
              </Box>
            </ItemContainer>
          </Box>
        ))}
      </TooltipContentBox>
    </TooltipContainer>
  )
}

export const NsspTimeSeries = ({ county }: { county: string }) => {
  const defaultDateRange = useMemo((): DateRange<Dayjs> => {
    const today = dayjsUTC()
    const sixMonthsAgo = today.subtract(6, "month")
    return [dayjsUTC(sixMonthsAgo), dayjsUTC(today)]
  }, [])

  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>(defaultDateRange)

  const { data: nsspData } = useListNsspNsspListGet({
    limit: 1000,
    county: county,
    week_ended_at_end: dateRange[1]?.format("YYYY-MM-DD"),
    week_ended_at_start: dateRange[0]?.format("YYYY-MM-DD"),
  })

  const { dataset, dates, series, legendItems } = useMemo(() => {
    if (!nsspData || nsspData.length === 0) {
      return {
        dataset: [],
        dates: [],
        series: [],
        legendItems: [],
      }
    }

    const firstDate = nsspData[0]?.week_ended_at
    if (!firstDate) {
      return {
        dataset: [],
        dates: [],
        series: [],
        legendItems: [],
      }
    }

    const maxDate = nsspData.reduce((max, d) => {
      const date = dayjsUTC(d.week_ended_at)
      return date.isAfter(max) ? date : max
    }, dayjsUTC(firstDate))

    const sixMonthsAgo = maxDate.subtract(6, "month")

    const filteredData = nsspData.filter(
      d =>
        dayjsUTC(d.week_ended_at).isAfter(sixMonthsAgo) ||
        dayjsUTC(d.week_ended_at).isSame(sixMonthsAgo)
    )

    if (filteredData.length === 0) {
      return {
        dataset: [],
        dates: [],
        series: [],
        legendItems: [],
      }
    }

    const targets = [
      { id: "percent_visits_covid", label: "COVID-19" },
      { id: "percent_visits_influenza", label: "Influenza" },
      { id: "percent_visits_rsv", label: "RSV" },
    ]

    const realSeries = targets.map(
      (target, i) =>
        ({
          id: target.id,
          dataKey: target.id,
          label: target.label,
          shape: CLINICAL_SHAPES[i % CLINICAL_SHAPES.length] ?? "circle",
          color: DEFAULT_SERIES_COLORS[i % DEFAULT_SERIES_COLORS.length] ?? "",
          showMark: true,
          curve: "linear" as const,
          connectNulls: false,
          valueFormatter: (value: number | null) =>
            value !== null ? `${value.toFixed(2)}%` : "N/A",
        }) satisfies LineSeries
    )

    const items = realSeries.map(
      s =>
        ({
          id: s.id,
          label: s.label,
          shape: s.shape,
          color: s.color,
        }) satisfies NsspClinicalLegendItem
    )

    const dateGroups = new Map<string, Nssp>()
    filteredData.forEach(d => {
      dateGroups.set(d.week_ended_at, d)
    })

    const sortedEndDates = Array.from(dateGroups.keys()).sort()

    const { dataset: filledDataset, dates: denseDates } = fillWeeklyGaps(
      sortedEndDates,
      dateGroups,
      (dateObj, sample) => {
        const entry: DatasetEntry = { date: dateObj }
        targets.forEach(t => {
          const val = sample && isNsspKey(t.id) ? sample[t.id] : null
          entry[t.id] = typeof val === "number" ? val : null
        })
        return entry
      },
      dateObj => {
        const nullEntry: DatasetEntry = { date: dateObj }
        targets.forEach(t => {
          nullEntry[t.id] = null
        })
        return nullEntry
      }
    )

    return {
      dataset: filledDataset,
      dates: denseDates,
      series: realSeries,
      legendItems: items,
    }
  }, [nsspData])

  return (
    <ChartWithFilters
      title="ED Visits Due to Select Pathogens"
      tooltipContent={
        <DataSourceCard
          sourceName="NSSP ED Visits"
          dataOwner="U.S. CDC"
          description="The U.S. Centers for Disease Control and Prevention's (CDC) National Syndromic Surveillance Program (NSSP) provides weekly data on emergency department (ED) visits for COVID-19, influenza, and RSV for all 50 states, the District of Columbia, and Guam."
          sourceURL="https://data.cdc.gov/Public-Health-Surveillance/NSSP-Emergency-Department-Visit-Trajectories-by-St/rdmq-nq56/about_data"
        />
      }
      dataSourcePill={<GenericTag text="Honolulu County" />}
      filters={{ dateRange, onDateRangeChange: setDateRange }}
    >
      <TimeSeriesChart
        dataset={dataset}
        dates={dates}
        series={series}
        legendItems={legendItems}
        yAxisLabel="PERCENT OF TOTAL ED VISITS"
        renderLegend={({ items, hiddenIds, onToggle, onToggleAll }) => (
          <TimeLegend
            items={items}
            hiddenIds={hiddenIds}
            onToggle={onToggle}
            onToggleAll={onToggleAll}
            helperText="Select a row to isolate it in the chart"
            headerTitle="PATHOGEN"
          />
        )}
        renderTooltip={hiddenIds => (
          <NsspClinicalTooltipContent
            rawData={nsspData}
            hiddenIds={hiddenIds}
          />
        )}
      />
    </ChartWithFilters>
  )
}
